import { useAuth } from '../contexts/AuthContext';
import type { CentralEdgeDevice } from '@simcheck/shared-types';

export function useDeviceVisibility(): DeviceVisibilityFlags {
  const { isSuperAdmin, activeOrganization, activeOrganizationId } = useAuth();
  return {
    isSuperAdmin: isSuperAdmin || false,
    isDemoOrg: activeOrganization?.settings?.isDemoOrg || false,
    hasLabAccess: activeOrganization?.settings?.hasLabAccess || false,
    activeOrganizationId: activeOrganizationId || null,
  };
}

export interface DeviceVisibilityFlags {
  isSuperAdmin: boolean;
  isDemoOrg: boolean;
  hasLabAccess: boolean;
  /** Org the viewer is currently switched into — scopes synthetic demo devices. */
  activeOrganizationId: string | null;
}

export function getDeviceTier(device: CentralEdgeDevice): 'production' | 'demo' | 'lab' {
  return device.deviceTier || (device.isDemo ? 'demo' : 'production');
}

/**
 * Synthetic demo devices (`syntheticDemo: true`) are seeded demo furniture with no
 * hardware behind them — e.g. the AutoConnect Global fleet. They carry
 * `deviceTier: 'production'` so they look like a normal fleet *inside* their owning
 * org, which means the tier rules below would let them leak everywhere. So they get
 * their own hard scope: visible ONLY while the viewer is switched into the owning
 * org. This deliberately sits ABOVE the super-admin bypass — a super-admin browsing
 * the global device view (or any other tenant) must not see them; they reappear as
 * soon as the super-admin switches into that org.
 *
 * Other tenants are already blocked server-side by `isVisibleToCaller` (ownerOrgId +
 * membership); this rule is what additionally hides them from super-admins in the UI.
 */
function isSyntheticDemoVisible(
  device: CentralEdgeDevice,
  activeOrganizationId: string | null,
): boolean {
  if (!(device as { syntheticDemo?: boolean }).syntheticDemo) return true;
  return !!activeOrganizationId && device.ownerOrgId === activeOrganizationId;
}

/**
 * Drop synthetic demo devices that don't belong to the org the viewer is currently
 * in. Use on surfaces that count/list devices without the full tier filter (e.g. the
 * TopBar live-status counter), so the demo fleet never inflates a global tally.
 */
export function filterSyntheticDemoDevices<T extends CentralEdgeDevice>(
  devices: T[],
  activeOrganizationId: string | null,
): T[] {
  return devices.filter(d => isSyntheticDemoVisible(d, activeOrganizationId));
}

export function filterDevicesByTier(
  devices: CentralEdgeDevice[],
  flags: DeviceVisibilityFlags
): CentralEdgeDevice[] {
  return devices.filter(d => {
    // Hard org scope for synthetic demo devices — applies to super-admin too.
    if (!isSyntheticDemoVisible(d, flags.activeOrganizationId)) return false;
    if (flags.isSuperAdmin) return true;
    const tier = getDeviceTier(d);
    if (tier === 'demo') return flags.isDemoOrg;
    if (tier === 'lab') return flags.hasLabAccess;
    return true;
  });
}
